import type { ReactNode } from 'react';
import { AuthProvider } from 'react-oidc-context';

import { missingConfigKeys } from '@/config';
import { config } from '@/config';
import { AuthScreen } from '@/features/auth/AuthScreen';
import { AUTH_CONFIG_KEYS, buildOidcSettings } from '@/features/auth/oidcSettings';
import { useSession } from '@/features/auth/useSession';

/**
 * Strips `?code=...&state=...` from the address bar once oidc-client-ts has
 * exchanged them. Route-level navigation back to where the user started is the
 * CallbackPage's job — this only keeps the one-time-use code out of the URL
 * (and out of anything that copies it, like a bookmark or a bug report).
 */
function onSigninCallback() {
  window.history.replaceState({}, document.title, window.location.pathname);
}

/**
 * Mounts `useSession()` for its side effect only: folding whoever is
 * currently signed in into `AccountStore` (`accounts.ts`). Mounted here,
 * inside `AuthProvider` but above the app's routes, so switching accounts
 * updates the store even before anything renders `AccountMenu` (WP-08).
 */
function SessionSync() {
  useSession();
  return null;
}

/**
 * Mounts `react-oidc-context` wired to the runtime configuration. Auth cannot
 * limp along without its settings the way the Chunk 0 shell could, so missing
 * Keycloak configuration is a hard failure here: a full-page explanation
 * instead of a provider that would redirect into nowhere.
 */
export function AppAuthProvider({ children }: { children: ReactNode }) {
  const missing = missingConfigKeys().filter((key) => AUTH_CONFIG_KEYS.includes(key));
  if (missing.length > 0) {
    return (
      <AuthScreen title="Configuration incomplete">
        <p>
          Sign-in is not possible: missing <strong>{missing.join(', ')}</strong>. Set them in{' '}
          <code>.env.local</code> for local development (see <code>.env.example</code>), or in the
          container environment for a deployment.
        </p>
      </AuthScreen>
    );
  }

  return (
    <AuthProvider {...buildOidcSettings(config)} onSigninCallback={onSigninCallback}>
      <SessionSync />
      {children}
    </AuthProvider>
  );
}
